import { useEffect, useMemo, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useApiClientWrapper } from 'hooks';
import { RootState } from 'store';
import { fetchAutomationOptionsThunk } from '@spike/automations-action';
import {
    type AutomationOptions,
    type ScheduleAnchor,
    type SubjectField,
    type TriggerEventOption
} from '@spike/automation-model';

/**
 * Trigger events, schedule anchors and subject fields for this marketplace.
 *
 * The event list used to be hardcoded; four of the seven it offered were never
 * dispatched, so automations built on them never fired.
 */
export interface AutomationOptionsState {
    options?: AutomationOptions;
    events: TriggerEventOption[];
    groupedEvents: Array<{ category: string; label: string; events: TriggerEventOption[] }>;
    anchors: ScheduleAnchor[];
    groupedAnchors: Array<{ category: string; label: string; anchors: ScheduleAnchor[] }>;
    loading: boolean;
    subjectFieldsFor: (subject?: string | null) => SubjectField[];
    subjectForEvent: (eventType: string) => string | null;
    subjectForAnchor: (anchorKey: string) => string | null;
}

export const useAutomationOptions = (): AutomationOptionsState => {
    const dispatch = useDispatch();
    const apiClientWrapper = useApiClientWrapper();

    const options = useSelector((state: RootState) => state.automations.options);

    // See useCriteriaCatalog: the api wrapper is a fresh object every render.
    const requested = useRef(false);

    useEffect(() => {
        if (requested.current) return;
        requested.current = true;

        (dispatch as any)(fetchAutomationOptionsThunk(apiClientWrapper)).catch(() => {
            requested.current = false;
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [options]);

    return useMemo(() => {
        const events = options?.triggerEvents ?? [];
        const anchorCatalog = options?.scheduleAnchors;
        const anchors = anchorCatalog?.anchors ?? [];

        const groupedEvents = [...(options?.eventCategories ?? [])]
            .sort((a, b) => a.order - b.order)
            .map(category => ({
                category: category.id as string,
                label: category.label,
                events: events.filter(event => event.category === category.id)
            }))
            .filter(group => group.events.length > 0);

        const groupedAnchors = [...(anchorCatalog?.categories ?? [])]
            .sort((a, b) => a.order - b.order)
            .map(category => ({
                category: category.id as string,
                label: category.label,
                anchors: anchors.filter(anchor => anchor.category === category.id)
            }))
            .filter(group => group.anchors.length > 0);

        return {
            options,
            events,
            groupedEvents,
            anchors,
            groupedAnchors,
            loading: !options,
            subjectFieldsFor: (subject?: string | null) => (subject ? options?.subjectFields?.[subject] ?? [] : []),
            subjectForEvent: (eventType: string) => events.find(e => e.type === eventType)?.subject ?? null,
            subjectForAnchor: (anchorKey: string) => anchors.find(a => a.key === anchorKey)?.subject ?? null
        };
    }, [options]);
};

export default useAutomationOptions;
